// Centralized API Configuration
// This is the SINGLE source of truth for all API URLs in the application

/*
 * API CONFIGURATION TROUBLESHOOTING
 * =================================
 * 
 * CRITICAL: If login fails with connection errors, check these first:
 * 
 * 1. PORT MISMATCH (Most Common Issue):
 *    - Frontend expects API on: NEXT_PUBLIC_API_URL (check ui/.env.local)
 *    - Backend actually runs on: port 9000 (see start.sh)
 *    - Fix: Set NEXT_PUBLIC_API_URL=http://localhost:9000 in ui/.env.local
 *    - Verify: curl http://localhost:9000/health should return {"status":"OK"}
 * 
 * 2. ENVIRONMENT VARIABLES:
 *    - Next.js requires NEXT_PUBLIC_ prefix for client-side access
 *    - Must restart Next.js dev server after changing .env.local
 *    - Check: Console should show API CONFIG log in development
 * 
 * 3. COMMON PORT CONFUSION:
 *    - Frontend (Next.js): 3000
 *    - Backend (FastAPI): 9000 ← This is what API_BASE_URL should point to
 *    - NOT 8000, 8003, or any other port unless explicitly configured
 * 
 * 4. CORS ISSUES:
 *    - Backend CORS must allow http://localhost:3000 (frontend URL)
 *    - Check apip/src/server.py CORSMiddleware configuration
 * 
 * 5. DEBUGGING STEPS:
 *    - Check browser Network tab for actual request URLs
 *    - Look for 'API CONFIG' log in browser console (development only)
 *    - Test API directly: curl http://localhost:9000/api/v1/auth/login
 *    - Verify server is running: ps aux | grep uvicorn
 */

// Import centralized configuration
import {
  getApiBaseUrl,
  buildCodeExecutionApiUrl,
  buildApiUrl as centralBuildApiUrl,
  buildDatabaseExecutionApiUrl as centralBuildDatabaseExecutionApiUrl
} from './api-central'

// Use centralized configuration
export const API_BASE_URL = getApiBaseUrl()

// Old dynamic loading system removed - now using simple centralized config from api-central.ts
export const API_VERSION = process.env.NEXT_PUBLIC_API_VERSION || 'v1'
export const API_KEY = process.env.NEXT_PUBLIC_API_KEY || 'dev-api-key-12345'
export const GOOGLE_CLIENT_ID = '737277702277-02kcbpka96q8s6i2p0m5j4t1rarfmj47.apps.googleusercontent.com'//process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID
export const IS_DEVELOPMENT = process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' || process.env.NODE_ENV === 'development'

// Removed old normalization logic - now handled in api-central.ts

// Coding Challenge API Configuration - uses centralized config
// Note: Code execution always uses remote API (Lambda sandboxes), other endpoints use normal selection
export const CODING_CHALLENGE_API_URL = getApiBaseUrl()

// Database Execution API Configuration - uses centralized config
export const DATABASE_EXECUTION_API_URL = getApiBaseUrl()

// Only log API config in development
if (IS_DEVELOPMENT) {
  console.log('🔧 API CONFIG:', {
    'API_BASE_URL': API_BASE_URL,
    'CODING_CHALLENGE_API_URL': CODING_CHALLENGE_API_URL,
    'DATABASE_EXECUTION_API_URL': DATABASE_EXECUTION_API_URL,
    'API_VERSION': API_VERSION,
    'environment': process.env.NEXT_PUBLIC_ENVIRONMENT || process.env.NODE_ENV
  })
}

/**
 * CENTRALIZED URL BUILDING FUNCTION
 * 
 * This is the SINGLE source of truth for all API URL construction.
 * Uses centralized configuration
 * 
 * @param endpoint - The API endpoint (e.g., '/auth/login', 'users/123')
 * @param method - HTTP method (default 'GET') - kept for backward compatibility
 * @returns Complete API URL with appropriate handling
 */
export const buildApiUrl = (endpoint: string, _method: string = 'GET'): string => {
  return centralBuildApiUrl(endpoint)
}

// Old complex URL building functions removed - now using simple centralized config

// Get standard headers with auth token
export const getApiHeaders = (includeAuth: boolean = true): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
  }

  if (includeAuth && typeof window !== 'undefined') {
    const token = localStorage.getItem('token')
    // Include Authorization header for all valid tokens
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
      console.log('🔍 API DEBUG: Added auth header with token:', token.substring(0, 30) + '...')
    } else {
      console.log('🔍 API DEBUG: No token found in localStorage')
    }
  } else {
    console.log('🔍 API DEBUG: Auth disabled or not in browser context')
  }

  console.log('🔍 API DEBUG: Final headers:', Object.keys(headers))
  return headers
}

// Standard fetch wrapper with centralized configuration
export const apiRequest = async (
  endpoint: string,
  methodOrOptions: string | RequestInit = 'GET',
  body?: unknown,
  additionalOptions: RequestInit = {}
): Promise<Response> => {
  const url = buildApiUrl(endpoint)

  let method = 'GET'
  let mergedOptions: RequestInit = {}
  let candidateBody: unknown = body

  if (typeof methodOrOptions === 'string') {
    method = methodOrOptions
    mergedOptions = { ...additionalOptions }
  } else {
    mergedOptions = { ...methodOrOptions, ...additionalOptions }
    if (methodOrOptions.method) {
      method = methodOrOptions.method
    }
    if (candidateBody === undefined && methodOrOptions.body !== undefined) {
      candidateBody = methodOrOptions.body
    }
  }

  if (mergedOptions.method) {
    method = mergedOptions.method
  }

  const headers = new Headers(getApiHeaders())
  if (mergedOptions.headers) {
    new Headers(mergedOptions.headers as HeadersInit).forEach((value, key) => {
      headers.set(key, value)
    })
  }

  let resolvedBody: BodyInit | null | undefined = null
  if (candidateBody !== undefined && candidateBody !== null) {
    resolvedBody = candidateBody as BodyInit
  } else if (mergedOptions.body !== undefined && mergedOptions.body !== null) {
    resolvedBody = mergedOptions.body as BodyInit
  }

  if (resolvedBody !== undefined && resolvedBody !== null) {
    if (resolvedBody instanceof FormData || resolvedBody instanceof Blob) {
      headers.delete('Content-Type')
    } else if (typeof resolvedBody === 'string') {
      // string is fine as-is
    } else {
      resolvedBody = JSON.stringify(resolvedBody)
    }
  }

  if ((resolvedBody === undefined || resolvedBody === null) && (method?.toUpperCase() ?? 'GET') === 'GET') {
    resolvedBody = undefined
  }

  const { headers: _ignoredHeaders, body: _ignoredBody, method: _ignoredMethod, ...restOptions } = mergedOptions

  const response = await fetch(url, {
    ...restOptions,
    method,
    headers: Object.fromEntries(headers.entries()),
    body: resolvedBody,
  })

  return response
}

/**
 * BUILD CODING CHALLENGE API URL
 * Uses centralized configuration - ALWAYS REMOTE for code execution
 */
export const buildCodingChallengeApiUrl = (endpoint: string, method: string = 'GET'): string => {
  // For code execution endpoints, ALWAYS use remote API (Lambda sandboxes) for security
  if (endpoint.includes('code/execute') || endpoint.includes('execute-with-question')) {
    return buildCodeExecutionApiUrl(endpoint)  // Always use remote API Gateway for code execution
  }
  // For other coding challenge endpoints, use normal API selection
  return buildApiUrl(endpoint)
}

/**
 * BUILD DATABASE EXECUTION API URL
 * Uses centralized configuration - ALWAYS REMOTE for database execution
 */
export const buildDatabaseExecutionApiUrl = (endpoint: string, _method: string = 'GET'): string => {
  return centralBuildDatabaseExecutionApiUrl(endpoint)
}

// Helper function to get the complete API URL for any endpoint
export const getApiUrl = (endpoint: string, method: string = 'GET'): string => {
  return buildApiUrl(endpoint, method)
}

// Helper function to get the complete coding challenge API URL
// Code execution endpoints always use remote API (Lambda sandboxes)
export const getCodingChallengeApiUrl = (endpoint: string, method: string = 'GET'): string => {
  return buildCodingChallengeApiUrl(endpoint, method)
}

// Helper function to get the complete database execution API URL
export const getDatabaseExecutionApiUrl = (endpoint: string, method: string = 'GET'): string => {
  return buildDatabaseExecutionApiUrl(endpoint, method)
}

// Direct export for code execution (always remote)
export { buildCodeExecutionApiUrl } from './api-central'

// Clear cached configuration (useful when config changes)
export const clearApiConfigCache = (): void => {
  // Cache was removed during migration; keep function for backward compatibility
  if (IS_DEVELOPMENT) {
    console.log('🔄 API configuration cache cleared (no-op)')
  }
}

// Preload configuration (useful for initialization)
export const preloadApiConfig = async (): Promise<void> => {
  // Configuration now resolves synchronously via api-central
  if (IS_DEVELOPMENT && typeof window !== 'undefined') {
    console.log('✅ API configuration ready (preload no-op)')
  }
}

// Backward compatibility exports
export { API_BASE_URL as default }
